import { formatCountryWithFlag } from "@/utils/countries";
import { getCountryFromEecs } from "@/utils/eecs";
import {
  AUTHORITY_ACCOUNT_MESSAGE_KEYS,
  isValidRegistryAccountNumber,
  type RegistrationAuthority,
  registryCountryForAccount,
} from "@/utils/registryAccounts";

/**
 * Recipient of a GO send, shared by the Send GO tab and the PPA SEND action editor.
 *
 * @property companyId - Set only for a hosted-account recipient picked from the search; such a
 *   recipient carries no registry account, Soldera resolves the destination per certificate
 * @property eecsAccountNumber - External recipient's registry account (EECS, Ofgem or TGE member code)
 * @property internalAccountNumber - Optional sub-account within the recipient's registry
 */
export interface SendRecipient {
  companyId: number | null;
  companyName: string;
  eecsAccountNumber: string;
  internalAccountNumber: string;
}

/** What the recipient search decides: the company picked or typed, and the account it proposes. */
export type RecipientSelection = Pick<
  SendRecipient,
  "companyId" | "companyName" | "eecsAccountNumber"
>;

/**
 * A recipient as the backend's `recipient_*` fields. A hosted recipient carries only its company;
 * an external one carries no company id, so a payload never holds both. Blank accounts become null.
 */
export interface RecipientFields {
  recipient_company_id: number | null;
  recipient_company_name: string;
  recipient_eecs_account_number: string | null;
  recipient_registry_internal_account_number: string | null;
}

export const EMPTY_SEND_RECIPIENT: SendRecipient = {
  companyId: null,
  companyName: "",
  eecsAccountNumber: "",
  internalAccountNumber: "",
};

const UNKNOWN_REGISTRY_ACCOUNT_MESSAGE_KEY =
  "go_send.account_invalid_unknown_registry";

export function isHostedRecipient(recipient: SendRecipient): boolean {
  return recipient.companyId != null;
}

/**
 * Registry country of an external recipient, derived from the account number.
 *
 * Under AIB the member-code prefix alone resolves, so the country shows while the number is
 * still being typed; RGP and REGO accounts carry no prefix and resolve only once valid. A null
 * authority (a PPA, whose registry is known only when the action executes) resolves only from a
 * recognized EECS or Ofgem account. Hosted recipients have no registry country.
 */
export function recipientRegistryCountry(
  recipient: SendRecipient,
  authority: RegistrationAuthority | null
): string | null {
  if (isHostedRecipient(recipient)) return null;
  const account = recipient.eecsAccountNumber.trim();
  return authority === "AIB"
    ? getCountryFromEecs(account)
    : registryCountryForAccount(account, authority);
}

/** The recipient's registry country with its flag, or "" when none derives. */
export function recipientCountryDisplay(
  recipient: SendRecipient,
  authority: RegistrationAuthority | null
): string {
  const country = recipientRegistryCountry(recipient, authority);
  return country ? formatCountryWithFlag(country) : "";
}

/**
 * i18n key describing why the external recipient's account number is unusable, or null.
 *
 * An empty account is not an error here (see isRecipientComplete). With a null authority the
 * account must be a recognized format with a derivable country, because the PPA stores that
 * country with the recipient.
 */
export function recipientAccountError(
  recipient: SendRecipient,
  authority: RegistrationAuthority | null
): string | null {
  const account = recipient.eecsAccountNumber.trim();
  if (isHostedRecipient(recipient) || !account) return null;
  if (authority === null) {
    return recipientRegistryCountry(recipient, null)
      ? null
      : UNKNOWN_REGISTRY_ACCOUNT_MESSAGE_KEY;
  }
  return isValidRegistryAccountNumber(account, authority)
    ? null
    : AUTHORITY_ACCOUNT_MESSAGE_KEYS[authority];
}

/** Whether the recipient is ready to send to: a name plus a hosted account or a valid registry account. */
export function isRecipientComplete(
  recipient: SendRecipient,
  authority: RegistrationAuthority | null
): boolean {
  if (!recipient.companyName.trim()) return false;
  if (isHostedRecipient(recipient)) return true;
  return (
    !!recipient.eecsAccountNumber.trim() &&
    recipientAccountError(recipient, authority) === null
  );
}

/** The backend `recipient_*` fields for a recipient; see RecipientFields. */
export function toRecipientFields(recipient: SendRecipient): RecipientFields {
  if (isHostedRecipient(recipient)) {
    return {
      recipient_company_id: recipient.companyId,
      recipient_company_name: recipient.companyName,
      recipient_eecs_account_number: null,
      recipient_registry_internal_account_number: null,
    };
  }
  return {
    recipient_company_id: null,
    recipient_company_name: recipient.companyName,
    recipient_eecs_account_number: recipient.eecsAccountNumber || null,
    recipient_registry_internal_account_number:
      recipient.internalAccountNumber || null,
  };
}

/** The recipient details an uploaded document's analysis read; any of them may be missing. */
export interface DocumentRecipientFields {
  recipient_company_name?: string | null;
  recipient_eecs_account_number?: string | null;
  recipient_registry_internal_account_number?: unknown;
}

/**
 * The recipient after an uploaded document. A document names an external recipient, never a hosted
 * account, so any recipient detail it supplies replaces a hosted pick. Fields the document leaves out
 * keep their current values.
 */
export function recipientFromDocument(
  current: SendRecipient,
  document: DocumentRecipientFields
): SendRecipient {
  const internalAccount = document.recipient_registry_internal_account_number;
  const recipient: SendRecipient = {
    ...current,
    companyName: document.recipient_company_name || current.companyName,
    eecsAccountNumber:
      document.recipient_eecs_account_number || current.eecsAccountNumber,
    internalAccountNumber: Object.prototype.hasOwnProperty.call(
      document,
      "recipient_registry_internal_account_number"
    )
      ? typeof internalAccount === "string"
        ? internalAccount.trim()
        : ""
      : current.internalAccountNumber,
  };
  const suppliesRecipient =
    !!document.recipient_company_name ||
    !!document.recipient_eecs_account_number ||
    !!recipient.internalAccountNumber;
  return suppliesRecipient ? { ...recipient, companyId: null } : recipient;
}
